import { models, stores } from '@kurtosys/app-start';
import { components } from '@kurtosys/ksys-app-components';
import { common } from '@kurtosys/ksys-app-template';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { EventType } from '@kurtosys/types/eventBus/EventType.js';
import { action, computed, makeObservable, observable } from 'mobx';

import { LIBRARY_COMPONENTS_CONFIGURATION } from '../../../configuration/libraryComponentsConfiguration.js';
import { MockData } from '../../../configuration/mockData/index.js';
import { IComponentStyles } from '../../../models/app/IComponentStyles.js';
import { IConfiguration } from '../../../models/app/IConfiguration.js';
import { TStoreContext } from '../../../models/app/TStoreContext.js';
import { Feature } from '../../shared/Feature.js';
import { IAppComponents } from '../models/IAppComponents.js';

import { QuizStore } from './QuizStore.js';
type ICardProps = components.base.Card.models.ICardProps;
type ITextProps = components.base.Text.models.ITextProps;
type ITextTooltip = components.base.Text.models.ITextTooltip;

/* [Component: appStoreComponentImport] */

let devConfig: models.IAppDevelopmentConfig = {
	applicationClientConfigurationIds: null,
	applicationClientConfigurations: null,
	authentication: null,
	configuration: null,
	styles: null,
	libraryComponentsConfiguration: LIBRARY_COMPONENTS_CONFIGURATION,
};

if (process.env.NODE_ENV !== 'production') {
	devConfig = {
		...devConfig,
		applicationClientConfigurationIds: async () => {
			const { APPLICATION_CLIENT_CONFIGURATION_IDS } = await import(
				'../../../configuration/development.applicationClientConfigurationIds.js'
			);
			return APPLICATION_CLIENT_CONFIGURATION_IDS;
		},
		applicationClientConfigurations: async () => {
			const { APPLICATION_CLIENT_CONFIGURATIONS } = await import(
				'../../../configuration/development.applicationClientConfigurations.js'
			);
			return APPLICATION_CLIENT_CONFIGURATIONS;
		},
		authentication: async () => {
			const { AUTHENTICATION } = await import('../../../configuration/development.authentication.js');
			return AUTHENTICATION;
		},
		configuration: async () => {
			const { CONFIGURATION } = await import('../../../configuration/development.config.js');
			return CONFIGURATION;
		},
		styles: async () => {
			const { STYLES } = await import('../../../configuration/development.styles.js');
			return STYLES;
		},
	};
}

export class AppStore extends stores.base.AppStoreBase<IConfiguration, IComponentStyles> {
	static EVENT_BUS_KEY: EventType = EventType.globalInputsChange;
	url: URL;
	@observable hideCommentary = false;
	@observable commentaryTooltip: ITextTooltip | undefined;

	constructor(
		element: HTMLElement,
		url: string,
		storeContext: TStoreContext,
		manifest: IManifest,
		mockData?: MockData,
	) {
		super(element, url, storeContext, manifest, Feature, devConfig, mockData);
		this.url = new URL(url);
		makeObservable(this);
	}

	@computed
	get quizStore() {
		return this.storeContext.get<QuizStore>('quizStore');
	}

	@computed
	get hasData(): boolean {
		// TODO: Each Application should put custom show logic here: "return this.storeContext[component store].hasData;"
		return !!(
			this.quizStore.article &&
			this.quizStore.article.questions &&
			this.quizStore.article.questions.length > 0
		);
	}

	@action
	contextsDidUpdateAfter = async () => {
		// TODO: Each Application should put custom logic here to handle changes to the data context
	};

	@action
	customDidRender(): Promise<void> {
		return this.quizStore.checkUnstructuredQuiz();
	}

	get components(): IAppComponents {
		return {
			/* [Component: appStoreComponent] */
		};
	}

	@computed
	get commentary(): string {
		if (this.appComponentConfiguration && this.appComponentConfiguration.commentary) {
			return this.getQueryValue(this.appComponentConfiguration.commentary);
		}
		return '';
	}

	@computed
	get textProps(): ITextProps {
		const props = this.appComponentConfiguration && this.appComponentConfiguration.textProps;
		const response: ITextProps = {
			value: this.commentary || '',
			tooltip: this.commentaryTooltip,
		};
		const textProps = common.commonUtils.deepMergeObjects(props || {}, response);
		return textProps;
	}

	@computed
	get completedCardProps(): ICardProps | undefined {
		return this.mergeQueriesAndProps(
			this.appComponentConfiguration && this.appComponentConfiguration.completedCardProps,
		);
	}

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	async customInitializeAfter() {
		this.quizStore.initialize();
	}

	@computed
	get articleId(): number {
		if (this.appComponentConfiguration && this.appComponentConfiguration.articleId) {
			return parseInt(this.getQueryValue(this.appComponentConfiguration.articleId));
		}
		return 0;
	}
}
